import React, { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { HeightMeasureInputMarkerIcon } from '../shared/components/icons/HeightMeasureInputMarkerIcon';
import clsx from 'clsx';
import { MEASURE_RANGE_VALUES } from '../state/child-details';

const ITEM_TYPE = 'MARKER';

const MARKER_CONTAINER_HEIGHT = 200; //size in px
const MARKER_ICON_HEIGHT = 44;
const TOP_OFFSET = (MARKER_CONTAINER_HEIGHT - MARKER_ICON_HEIGHT) / 2;

const getFixRange = (top: number) => {
  for (const range of MEASURE_RANGE_VALUES) {
    if (top >= range.startPixel && top <= range.endPixel) {
      return range;
    }
  }
  return null;
};

type MeasureMarkerProps = {
  onChange?: (value: number) => void;
  initialTop: number;
};

export const MeasureMarker: React.FC<MeasureMarkerProps> = ({ onChange, initialTop }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(initialTop);

  const [, drag, preview] = useDrag(
    () => ({
      type: ITEM_TYPE,
      item: { top },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [top]
  );

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: ITEM_TYPE,
      hover: (item: any, monitor) => {
        const delta = monitor.getDifferenceFromInitialOffset() as { x: number; y: number };
        let newTop = Math.round(item.top + delta.y);
        const newTopFixed = Math.min(410, Math.max(0, newTop));

        setTop(newTopFixed);

        const range = getFixRange(newTopFixed);

        range && onChange?.(range.average);
      },
      collect: (monitor) => ({
        isOver: !monitor.isOver(),
      }),
    }),
    [top]
  );

  useEffect(() => {
    if (isOver) {
      const range = getFixRange(top);

      if (!range) {
        return;
      }

      setTop(range.fixPixel);
      onChange?.(range.average);
    }
  }, [isOver, top, onChange, setTop]);

  useEffect(() => {
    if (ref.current) {
      const previewImg = new Image(1, 1);
      previewImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';
      preview(previewImg);
    }
  }, [preview]);

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={clsx('absolute flex items-center ml-5 -left-6.5 select-none', {
        'transition-all': isOver,
      })}
      style={{
        top: top - TOP_OFFSET,
        height: MARKER_CONTAINER_HEIGHT,
        ...(isOver ? { transitionDuration: '200ms' } : null),
      }}
    >
      <HeightMeasureInputMarkerIcon
        className="text-green-laser cursor-pointer "
        style={{ height: MARKER_ICON_HEIGHT }}
      />
    </div>
  );
};
